/** The custom tool as blocks, top to bottom: what the AI passes, then each step and where every argument comes from. */

import { useEffect, useRef, type MouseEvent } from 'react';
import { Plus, Rows3 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCustomToolBuilder } from '../builder/builder-context';
import { StepPicker } from '../builder/step-picker';
import { LIMITS } from '../custom-tool-draft';
import { FirstTool } from '../steps/first-tool';
import { SIDE_PANEL_CLEARANCE } from '../ui/side-panel';
import { InputsBlock } from './inputs-block';
import { StepBlock } from './step-block';

const AddTool = () => (
	<StepPicker>
		<button
			type='button'
			className='flex h-9 w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-gray-600 bg-white text-xs leading-4 text-gray-900 hover:bg-gray-50'
		>
			<Plus className='size-3.5' aria-hidden />
			Add tool
		</button>
	</StepPicker>
);

export const BlockList = () => {
	const { draft, focus, focusOn, testing } = useCustomToolBuilder();
	const list = useRef<HTMLDivElement>(null);
	const column = useRef<HTMLDivElement>(null);

	// The header's "what's left" link and a new step bring their block into view.
	useEffect(() => {
		if (!focus) return;
		const block = focus.kind === 'inputs' ? 'inputs' : `step-${focus.id}`;
		list.current?.querySelector(`[data-block="${block}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
	}, [focus]);

	// A click beside the blocks lets go of the focused one.
	const onBackgroundClick = (event: MouseEvent) => {
		if (event.target === list.current || event.target === column.current) focusOn(null);
	};

	return (
		// The open test panel covers the right side, so the blocks move clear of it.
		<div ref={list} onClick={onBackgroundClick} className={cn('min-h-0 flex-1 overflow-y-auto bg-gray-50', testing.open && SIDE_PANEL_CLEARANCE)}>
			<div ref={column} className='mx-auto flex max-w-3xl flex-col gap-4 px-6 py-6'>
				{draft.steps.length === 0 ? (
					<FirstTool icon={Rows3} hint='Then pick where each of their arguments comes from.' className='mx-auto mt-16 w-[340px]' />
				) : (
					<>
						<InputsBlock />
						{draft.steps.map((step, index) => (
							<StepBlock key={step.id} step={step} index={index} />
						))}
						{draft.steps.length < LIMITS.steps && <AddTool />}
					</>
				)}
			</div>
		</div>
	);
};
